<script lang="ts">
	import GlobeIcon from '$components/UI/Icons/GlobeIcon.svelte';
	import { getWeglotState } from '$lib/state/weglot.svelte';

	const weglot = getWeglotState();

	let isOpen = $state(false);

	function toggle() {
		isOpen = !isOpen;
	}

	function selectLanguage(code: string) {
		isOpen = false;
		weglot.switchTo(code);
	}

	function handleWindowClick(event: MouseEvent) {
		if (!isOpen) return;

		const target = event.target as HTMLElement;
		if (!target.closest('.language-switch')) {
			isOpen = false;
		}
	}

	function handleWindowKeydown(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			isOpen = false;
		}
	}
</script>

{#snippet checkMark()}
	<svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M4.04167 8.875L0 4.85417L1.0625 3.77083L4.04167 6.75L10.8125 0L11.875 1.0625L4.04167 8.875Z"
			fill="#4BE1A4"
		/>
	</svg>
{/snippet}

<svelte:window onclick={handleWindowClick} onkeydown={handleWindowKeydown} />

{#if weglot.hasAlternates()}
	<div class="header-action language-switch" class:is-open={isOpen}>
		<button type="button" class:is-active={isOpen} aria-expanded={isOpen} onclick={toggle}>
			<GlobeIcon />
		</button>

		<ul class="language-switch-dropdown">
			{#each weglot.languages() as code}
				{@const isCurrent = code === weglot.currentLanguage()}
				<li>
					<button type="button" class:is-current={isCurrent} onclick={() => selectLanguage(code)}>
						{weglot.languageName(code)}
						{#if isCurrent}
							<span class="language-switch-check">{@render checkMark()}</span>
						{/if}
					</button>
				</li>
			{/each}
		</ul>
	</div>
{/if}

<style lang="postcss" global>
	.language-switch {
		display: none;

		@media (--xl) {
			display: block;
		}
	}

	.language-switch-dropdown {
		position: absolute;
		z-index: 1;
		top: calc(100% + 26px);
		left: auto;
		right: 0;
		display: block;
		width: max-content;
		min-width: 220px;
		padding: 16px 12px;
		border-radius: 8px;
		background-color: var(--color-white);
		opacity: 0;
		visibility: hidden;
		transition:
			opacity var(--transition),
			visibility var(--transition);

		li {
			margin: 0;
			padding: 0;
			white-space: nowrap;

			button {
				display: flex;
				align-items: center;
				justify-content: space-between;
				gap: var(--spacing-3);
				width: 100%;
				height: auto;
				padding: 8px 12px;
				border: 0;
				border-radius: 8px;
				cursor: pointer;
				color: var(--color-brown);
				font: inherit;
				font-size: 18px;
				font-family: var(--font-primary);
				line-height: 1.6;
				text-align: left;
				transition: background-color var(--transition);

				&:hover {
					background-color: var(--color-primary-light);
				}
			}
		}

		.language-switch.is-open & {
			opacity: 1;
			visibility: visible;
			border: 1px solid #e0ddd9;
		}
	}

	.language-switch-check {
		display: flex;
		flex: 0 0 auto;
		width: 14px;
		color: var(--color-brown);
	}
</style>
